import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// components

// services
import { isRTL } from 'core/services/language';
import { Timeout } from 'react-number-format/types/types';
import { onGetPaymentStatus } from '../services/walletServices';
import { PaymentStatus } from 'modules/main/companyInformation/payments/models/payment';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import Icon from 'utils/icon';

// styles

// models

interface OrderStatusComponentProps {
	merchantId: string;
	onStatusChange: (status: 'success' | 'failed') => void;
}

const OrderStatusComponent: FunctionComponent<OrderStatusComponentProps> = ({ merchantId, onStatusChange }) => {
	const { t } = useTranslation();

	const [status, setStatus] = useState<PaymentStatus>('Pending');

	useEffect(() => {
		let timeoutId: Timeout;
		const startTime = Date.now();
		const poll = async () => {
			try {
				const response = await onGetPaymentStatus(merchantId);
				if (['Completed', 'Failed'].includes(response.status)) {
					setStatus(response.status);
					onStatusChange(response.status === 'Completed' ? 'success' : 'failed');
					return;
				}
				if (Date.now() - startTime < 60000) timeoutId = setTimeout(poll, 5000);
			} catch (e) {
				console.error(e);
			}
		};
		poll();
		return () => clearTimeout(timeoutId);
	}, []);

	return (
		<>
			{status == 'Pending' && (
				<motion.div key="pending" exit={{ opacity: 0, transform: 'translateY(-50px)' }} transition={{ duration: 0.5 }}>
					<motion.h2
						initial={{ opacity: 0, transform: 'translateY(50px)' }}
						animate={{ opacity: 1, transform: 'translateY(0)' }}
						transition={{ duration: 0.5 }}>
						{t('COMMON.CHECKING_PAYMENT_STATUS')}

						<div className="mt-5">
							<LoadingComponent />
						</div>
					</motion.h2>
				</motion.div>
			)}

			{status == 'Completed' && (
				<motion.div
					key="completed"
					initial={{ opacity: 0, transform: 'translateY(50px)' }}
					animate={{ opacity: 1, transform: 'translateY(0)' }}
					transition={{ duration: 0.5 }}
					style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 50 }}>
					<Icon name="success2" width={150} height={150} />
					<motion.h2
						initial={{ opacity: 0, transform: 'translateY(50px)' }}
						animate={{ opacity: 1, transform: 'translateY(0)' }}
						transition={{ duration: 0.5 }}>
						{t('COMMON.PAYMENT_SUCCESSFULLY')}
					</motion.h2>
				</motion.div>
			)}

			{status == 'Failed' && (
				<motion.div
					key="failed"
					initial={{ opacity: 0, transform: 'translateY(50px)' }}
					animate={{ opacity: 1, transform: 'translateY(0)' }}
					transition={{ duration: 0.5 }}
					style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 50 }}>
					<Icon name="failed2" width={150} height={150} />
					<div className="text-center">
						<motion.h2
							style={{ marginBottom: 20 }}
							initial={{ opacity: 0, transform: 'translateY(50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0)' }}
							transition={{ duration: 0.5 }}>
							{t('COMMON.PAYMENT_FAILED')}
						</motion.h2>

						<motion.h6
							initial={{ opacity: 0, transform: 'translateY(50px)' }}
							animate={{ opacity: 1, transform: 'translateY(0)' }}
							transition={{ duration: 0.5 }}>
							{t('COMMON.PAYMENT_FAILED_DESC')}
						</motion.h6>
					</div>
				</motion.div>
			)}
		</>
	);
};

export default OrderStatusComponent;
